/**
 * Analytics Tracking Utility
 * Tracks downloads, shares, page views, and other events
 */

import axios from 'axios';
import { getDeviceFingerprint } from './deviceFingerprint';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

// Get user's location (with permission)
export const getUserLocation = async () => {
  return new Promise((resolve) => {
    // First try IP-based geolocation (no permission needed)
    fetch('https://ipapi.co/json/')
      .then(res => res.json())
      .then(data => {
        resolve({
          country: data.country_name,
          city: data.city,
          region: data.region,
          lat: data.latitude,
          lon: data.longitude,
          ip: data.ip
        });
      })
      .catch(() => {
        // Fallback: try browser geolocation
        if (navigator.geolocation) {
          navigator.geolocation.getCurrentPosition(
            (position) => {
              resolve({
                lat: position.coords.latitude,
                lon: position.coords.longitude
              });
            },
            () => resolve(null),
            { timeout: 5000 }
          );
        } else {
          resolve(null);
        }
      });
  });
};

/**
 * Track an event
 * @param {string} eventType - Type of event: 'download', 'share', 'page_view', 'premium_click'
 * @param {object} metadata - Additional event data
 */
export const trackEvent = async (eventType, metadata = {}) => {
  try {
    const [fingerprint, location] = await Promise.all([
      getDeviceFingerprint(),
      getUserLocation()
    ]);

    await axios.post(`${API}/analytics/track`, {
      event_type: eventType,
      device_fingerprint: fingerprint,
      location: location,
      metadata: metadata,
      user_agent: navigator.userAgent
    });

    console.log(`[Analytics] Tracked: ${eventType}`);
    return true;
  } catch (error) {
    console.error('[Analytics] Failed to track event:', error);
    return false;
  }
};

// Convenience methods
export const trackDownload = (metadata = {}) => trackEvent('download', metadata);
export const trackShare = (platform = 'unknown') => trackEvent('share', { platform });
export const trackPageView = (page = window.location.pathname) => trackEvent('page_view', { page });
export const trackPremiumClick = () => trackEvent('premium_click', {});

// Track page view on component mount
export const usePageViewTracking = () => {
  if (typeof window !== 'undefined') {
    // Only track once per session per page
    const key = `tracked_${window.location.pathname}`;
    if (!sessionStorage.getItem(key)) {
      trackPageView();
      sessionStorage.setItem(key, 'true');
    }
  }
};

export default {
  trackEvent,
  trackDownload,
  trackShare,
  trackPageView,
  trackPremiumClick,
  getUserLocation
};
